import { useCallback, useEffect, useRef } from 'react';

import type { NoteTelemetryEventType, SelectionMetrics } from '../utils/analytics/noteTelemetry';
import { type AnyBlock, type AnyBlockNoteEditor } from '../utils/blocknote/types';

type Section = {
  id: string;
  label: string;
  headingElement: HTMLElement;
  level: number;
  index: number;
};

export type AnalyticsHookOptions = {
  docId: string;
  getDocVersion: () => string; // increment when content changes
  settleMs?: number; // selection "settled" threshold (default 250ms)
  throttleMs?: number; // selection change throttle (default 100ms)
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
  sections?: {
    enabled?: boolean;
    headingLevels?: number[];
  };
  onEvent: (type: NoteTelemetryEventType, payload: SelectionMetrics) => void;
};

/**
 * Hook to track BlockNote editor selection and copy analytics
 * Provides metrics on selection size, timing, modality, and copy events
 */
export function useBlockNoteSelectionAnalytics(
  editor: AnyBlockNoteEditor | undefined,
  editorRootRef: React.RefObject<HTMLDivElement | null>,
  opts: AnalyticsHookOptions,
) {
  const settleMs = opts.settleMs ?? 1000; // 1 second default
  const throttleMs = opts.throttleMs ?? 100;
  const sectionsEnabled = opts.sections?.enabled === true;
  const headingLevels = opts.sections?.headingLevels ?? [2]; // Default to h2 only

  // Section detection state
  const sectionsRef = useRef<Section[]>([]);

  // Running state for a selection gesture
  const state = useRef<{
    selectionId?: string;
    startedAt?: number;
    lastChangedAt?: number;
    lastLen: number;
    totalChars: number;
    settleTimer?: number;
    lastInput?: 'mouse' | 'touch' | 'keyboard';
  }>({ lastLen: 0, totalChars: 0 });

  // Timestamp of the last detected keyboard copy chord (Cmd/Ctrl + C) within this editor
  const lastKeyboardCopyAtRef = useRef<number>(0);

  const now = () => performance.now();
  const uuid = () => crypto.randomUUID();

  // Utilities
  const computeBlockPlainLen = (block: AnyBlock | null | undefined): number => {
    if (!block) return 0;
    let n = 0;
    if (Array.isArray(block.content)) {
      for (const ic of block.content as any[]) {
        if (!ic) continue; // Skip undefined content items
        if (ic.type === 'text') {
          n += (ic.text || '').length;
        } else if (ic.content?.length) {
          for (const st of ic.content) {
            if (st) {
              n += (st.text || '').length;
            }
          }
        }
      }
    } else if (typeof (block as any).content === 'string') {
      n += ((block as any).content as string).length;
    }
    if (block.children?.length) {
      for (const ch of block.children) {
        if (ch) {
          n += computeBlockPlainLen(ch);
        }
      }
    }
    return n + 1; // newline separator per block (optional)
  };

  const computeTotalChars = (): number => {
    if (!editor || !editor.document) return 0;
    let sum = 0;
    // Filter out any undefined blocks before processing
    const validBlocks = editor.document.filter(block => block != null);
    for (const b of validBlocks) {
      sum += computeBlockPlainLen(b);
    }
    return Math.max(0, sum - 1); // remove trailing extra newline
  };

  // Find all headings in the editor DOM
  const findHeadings = useCallback((): Section[] => {
    const root = editorRootRef.current;
    if (!root) return [];

    // Try standard heading selectors first
    const headingSelectors = headingLevels.map(level => `h${level}`).join(',');
    let headingElements = Array.from(root.querySelectorAll(headingSelectors));

    // Fallback: try BlockNote-specific data attributes
    if (headingElements.length === 0) {
      headingElements = Array.from(
        root.querySelectorAll('[data-node-type="heading"], [data-content-type="heading"]'),
      );
    }

    // Build sections array
    const sections: Section[] = [];
    headingElements.forEach((heading, index) => {
      const level = parseInt(heading.tagName.charAt(1)) || 1;
      if (headingLevels.includes(level)) {
        const label = heading.textContent?.trim() || `Section ${index + 1}`;
        const sectionId = `${opts.docId}:h${level}:${index}`;
        sections.push({
          id: sectionId,
          label,
          headingElement: heading as HTMLElement,
          level,
          index,
        });
      }
    });

    return sections;
  }, [editorRootRef, headingLevels, opts.docId]);

  // Find which section a DOM element belongs to
  const findSectionForElement = useCallback(
    (element: HTMLElement | null): Section | null => {
      if (!element || sectionsRef.current.length === 0) return null;

      const root = editorRootRef.current;
      if (!root || !root.contains(element)) return null;

      // Check if element is a heading itself
      const directHeadingMatch = sectionsRef.current.find(
        s => s.headingElement === element || s.headingElement.contains(element),
      );
      if (directHeadingMatch) {
        return directHeadingMatch;
      }

      // Find the last heading that comes before this element in DOM order
      let bestMatch: Section | null = null;
      let bestPosition = -1;

      for (let i = sectionsRef.current.length - 1; i >= 0; i--) {
        const section = sectionsRef.current[i];
        const position = section.headingElement.compareDocumentPosition(element);

        // Check if heading comes before element (heading → element)
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
          // Use the last (most recent) heading that precedes the element
          if (i > bestPosition) {
            bestPosition = i;
            bestMatch = section;
          }
        }
      }

      // If no preceding heading found, return first section
      return bestMatch || sectionsRef.current[0] || null;
    },
    [editorRootRef],
  );

  // Split a selection range into per-section ranges for multi-section selections
  // This is only used for local BlockNote sections, not for JotDoc (which uses the shared bridge)
  const getSelectionBySections = useCallback((): Array<{
    sectionId: string;
    sectionTitle: string;
    selectedText: string;
    sectionTotalChars: number;
  }> => {
    // Only works with local sections, not shared bridge sections (JotDoc)
    if (!sectionsEnabled || !editorRootRef.current) return [];

    // Don't use this for JotDoc context - check if we're using shared bridge
    const root = editorRootRef.current as any;
    if (root.__sectionAnalytics) {
      // Using shared bridge (JotDoc), don't use local section splitting
      return [];
    }

    // Must have local sections populated
    if (sectionsRef.current.length === 0) return [];

    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return [];

    try {
      const range = selection.getRangeAt(0);

      // Find all sections that intersect with the selection
      const intersectingSections: Array<{
        sectionId: string;
        sectionTitle: string;
        selectedText: string;
        sectionTotalChars: number;
      }> = [];

      for (let i = 0; i < sectionsRef.current.length; i++) {
        const section = sectionsRef.current[i];
        const nextSection = sectionsRef.current[i + 1];

        // Create a range for this section's content
        const sectionRange = document.createRange();
        sectionRange.setStartAfter(section.headingElement);
        if (nextSection) {
          sectionRange.setEndBefore(nextSection.headingElement);
        } else {
          sectionRange.setEnd(root, root.childNodes.length);
        }

        // Check if selection intersects with this section
        const selectionIntersects =
          range.compareBoundaryPoints(Range.START_TO_END, sectionRange) > 0 &&
          range.compareBoundaryPoints(Range.END_TO_START, sectionRange) < 0;

        if (selectionIntersects) {
          // Create an intersection range
          const intersectionRange = document.createRange();

          // Set start to the later of the two starts
          if (range.compareBoundaryPoints(Range.START_TO_START, sectionRange) > 0) {
            intersectionRange.setStart(range.startContainer, range.startOffset);
          } else {
            intersectionRange.setStartAfter(section.headingElement);
          }

          // Set end to the earlier of the two ends
          if (range.compareBoundaryPoints(Range.END_TO_END, sectionRange) < 0) {
            intersectionRange.setEnd(range.endContainer, range.endOffset);
          } else if (nextSection) {
            intersectionRange.setEndBefore(nextSection.headingElement);
          } else {
            intersectionRange.setEnd(root, root.childNodes.length);
          }

          const selectedText = intersectionRange.toString();
          const sectionTotalText = sectionRange.toString();

          if (selectedText.length > 0) {
            intersectingSections.push({
              sectionId: section.id,
              sectionTitle: section.label,
              selectedText,
              sectionTotalChars: sectionTotalText.length,
            });
          }
        }
      }

      return intersectingSections;
    } catch (error) {
      console.error('[BlockNote] Error splitting selection by sections:', error);
      return [];
    }
  }, [sectionsEnabled, editorRootRef]);

  // Resolve section id/title for the current selection using the shared section analytics when available
  const getSectionInfoForSelection = useCallback((): { id?: string; title?: string } => {
    if (!sectionsEnabled || !editorRootRef.current) return {};

    const root = editorRootRef.current as any;
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0) return {};

    try {
      // Prefer shared bridge if available
      if (root.__sectionAnalytics?.getSectionInfoFromSelection) {
        try {
          const info = root.__sectionAnalytics.getSectionInfoFromSelection();
          if (info && (info.id || info.title)) return info;
        } catch {}
      }

      const range = selection.getRangeAt(0);
      const node = range.commonAncestorContainer;
      let element: HTMLElement | null = null;
      if (node.nodeType === Node.TEXT_NODE) {
        element = node.parentElement;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        element = node as HTMLElement;
      }
      if (!element) return {};

      // Prefer the section id from the markdown analytics helper (authoritative)
      let id: string | undefined;
      let title: string | undefined;

      if (root.__sectionAnalytics?.getSectionIdForElement) {
        try {
          id = root.__sectionAnalytics.getSectionIdForElement(element) || undefined;
          // Get title from the same helper if available
          if (id && root.__sectionAnalytics?.getSectionTitleForId) {
            try {
              title = root.__sectionAnalytics.getSectionTitleForId(id);
            } catch {}
          }
        } catch {}
      }

      // Fallback: Derive title by mapping the element to a section using our local index
      if (!title) {
        const sectionFromElement = findSectionForElement(element);
        if (sectionFromElement) {
          title = sectionFromElement.label;
          // If id missing, fall back to local computed id
          if (!id) id = sectionFromElement.id;
        } else if (id) {
          // If we have id, try to find matching section for title in our local cache
          const byId = sectionsRef.current.find(s => s.id === id);
          if (byId) title = byId.label;
        }
      }

      return { id, title };
    } catch {
      return {};
    }
  }, [sectionsEnabled, editorRootRef, findSectionForElement]);

  // Compute approximate text length of a section by DOM range between its heading and the next heading
  // Prefer the shared bridge if available (for JotDoc viewer context)
  const getSectionCharCountById = useCallback(
    (sectionId: string | undefined): number | undefined => {
      if (!sectionId || !editorRootRef.current) return undefined;

      // Try shared bridge first (for JotDoc viewer)
      const root = editorRootRef.current as any;
      if (root.__sectionAnalytics?.getSectionCharCountById) {
        try {
          const count = root.__sectionAnalytics.getSectionCharCountById(sectionId);
          if (count !== undefined) return count;
        } catch {}
      }

      // Fallback to local sections (for inline BlockNote editors)
      const idx = sectionsRef.current.findIndex(s => s.id === sectionId);
      if (idx === -1) return undefined;
      const startHeading = sectionsRef.current[idx]?.headingElement;
      const nextHeading = sectionsRef.current[idx + 1]?.headingElement || null;
      if (!startHeading) return undefined;
      try {
        const range = document.createRange();
        range.setStartAfter(startHeading);
        if (nextHeading) {
          range.setEndBefore(nextHeading);
        } else {
          range.setEnd(root, root.childNodes.length);
        }
        const text = range.toString();
        return text ? text.length : 0;
      } catch {
        return undefined;
      }
    },
    [editorRootRef],
  );

  // Get section title for current selection
  const getSectionTitleForSelection = useCallback((): string | undefined => {
    if (!sectionsEnabled || !editorRootRef.current) return undefined;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return undefined;

    try {
      const range = selection.getRangeAt(0);
      const startElement = range.startContainer;

      // Get the containing element (if startContainer is a text node, get its parent)
      let element: HTMLElement | null = null;
      if (startElement.nodeType === Node.TEXT_NODE) {
        element = startElement.parentElement;
      } else if (startElement.nodeType === Node.ELEMENT_NODE) {
        element = startElement as HTMLElement;
      }

      if (!element) return undefined;

      const section = findSectionForElement(element);
      return section?.label;
    } catch {
      return undefined;
    }
  }, [sectionsEnabled, findSectionForElement]);

  const isBackward = (): boolean | undefined => {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return undefined;

    // backward if focus comes before anchor in document order
    try {
      const range = document.createRange();
      range.setStart(sel.anchorNode!, sel.anchorOffset);
      range.setEnd(sel.focusNode!, sel.focusOffset);
      return (
        range.collapsed &&
        (sel.anchorNode !== sel.focusNode || sel.anchorOffset !== sel.focusOffset)
      );
    } catch {
      return undefined;
    }
  };

  // Initialize sections and keep them updated
  useEffect(() => {
    if (!sectionsEnabled || !editorRootRef.current) return;

    // Initial scan
    sectionsRef.current = findHeadings();

    // Set up MutationObserver to detect content changes
    let rescanTimer: number | null = null;
    const scheduleRescan = () => {
      if (rescanTimer !== null) {
        clearTimeout(rescanTimer);
      }
      rescanTimer = window.setTimeout(() => {
        sectionsRef.current = findHeadings();
        rescanTimer = null;
      }, 150);
    };

    const mutationObserver = new MutationObserver(() => {
      scheduleRescan();
    });

    mutationObserver.observe(editorRootRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
      if (rescanTimer !== null) {
        clearTimeout(rescanTimer);
        rescanTimer = null;
      }
    };
  }, [sectionsEnabled, findHeadings]);

  // Initialize total chars & keep fresh on changes
  useEffect(() => {
    if (!editor) return;
    state.current.totalChars = computeTotalChars();
  }, [editor]);

  // Track document changes to update total chars
  // PERFORMANCE: Debounced to avoid expensive computation on every keystroke
  useEffect(() => {
    if (!editor) return;

    let debounceTimer: number | null = null;

    const unsubscribe = editor.onChange(() => {
      // Clear existing timer to reset debounce
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }

      // Debounce character counting (500ms) to avoid blocking on every keystroke
      debounceTimer = window.setTimeout(() => {
        state.current.totalChars = computeTotalChars();
        debounceTimer = null;
      }, 500);
    });

    return () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [editor]);

  // Modality tracker (consistent across contexts): pointerdown + keydown
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;

    const controller = new AbortController();
    const { signal } = controller;

    const onPointerDown = (e: PointerEvent) => {
      state.current.lastInput = e.pointerType === 'touch' ? 'touch' : 'mouse';
    };
    const onKeyDown = () => {
      state.current.lastInput = 'keyboard';
    };

    root.addEventListener('pointerdown', onPointerDown, { signal });
    root.addEventListener('keydown', onKeyDown, { signal });

    return () => {
      controller.abort();
    };
  }, [editorRootRef]);

  // Fallback modality tracker at the document level to catch keyboard/pointer events
  // that originate within the editor but don't bubble to the root (e.g., Cmd/Ctrl+C)
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;

    const controller = new AbortController();
    const { signal } = controller;

    const onDocPointerDown = (e: PointerEvent) => {
      if (root.contains(e.target as Node)) {
        state.current.lastInput = e.pointerType === 'touch' ? 'touch' : 'mouse';
      }
    };
    const onDocKeyDown = (e: KeyboardEvent) => {
      const targetInside = root.contains(e.target as Node);
      if (targetInside) {
        state.current.lastInput = 'keyboard';
      }

      // Detect copy chord (Cmd/Ctrl + C). Some platforms target body/document; validate selection is inside root
      if ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'C')) {
        const sel = window.getSelection?.();
        if (sel && sel.rangeCount > 0) {
          try {
            const range = sel.getRangeAt(0);
            if (root.contains(range.commonAncestorContainer)) {
              state.current.lastInput = 'keyboard';
              lastKeyboardCopyAtRef.current = performance.now();
            }
          } catch {}
        }
      }
    };

    document.addEventListener('pointerdown', onDocPointerDown, { capture: true, signal });
    document.addEventListener('keydown', onDocKeyDown, { capture: true, signal });

    return () => {
      controller.abort();
    };
  }, [editorRootRef]);

  // Throttle helper
  const lastFire = useRef(0);
  const shouldThrottle = () => {
    const t = now();
    if (t - lastFire.current < throttleMs) return true;
    lastFire.current = t;
    return false;
  };

  // Selection change pipeline
  useEffect(() => {
    if (!editor) return;

    const handleSelectionChange = async () => {
      if (shouldThrottle()) return;

      const selectedText = editor.getSelectedText();
      const len = selectedText.length;
      const t = now();

      // Start of a new selection gesture
      if (len > 0 && !state.current.startedAt) {
        state.current.selectionId = uuid();
        state.current.startedAt = t;
      }

      // Update timing + (re)arm settle timer
      if (len > 0) {
        state.current.lastChangedAt = t;
        if (state.current.settleTimer) {
          clearTimeout(state.current.settleTimer);
        }

        state.current.settleTimer = window.setTimeout(async () => {
          if (!state.current.startedAt || !editor) return;

          // Capture current selection state when timer fires (selection may have changed)
          const currentSelectedText = editor.getSelectedText();
          const currentLen = currentSelectedText.length;
          const settleTime = now();

          // Only fire if there's still a selection
          if (currentLen === 0) return;

          const { id: sectionId, title: sectionTitle } = getSectionInfoForSelection();

          // Track the select event in section analytics if available
          if (
            sectionId &&
            editorRootRef.current &&
            (editorRootRef.current as any).__sectionAnalytics?.trackTextSelected
          ) {
            try {
              (editorRootRef.current as any).__sectionAnalytics.trackTextSelected(sectionId);
            } catch {}
          }

          const sectionTotal = getSectionCharCountById(sectionId);
          const percentBase =
            sectionTotal && sectionTotal > 0 ? sectionTotal : state.current.totalChars;
          const calculatedPercent = percentBase ? currentLen / percentBase : 0;

          const payload: SelectionMetrics = {
            selectionId: state.current.selectionId!,
            docId: opts.docId,
            docVersion: opts.getDocVersion(),
            chars: {
              selected: currentLen,
              total: state.current.totalChars,
              percent: calculatedPercent,
            },
            timings: {
              startedAt: state.current.startedAt,
              lastChangedAt: state.current.lastChangedAt!,
              settledAt: settleTime,
              msToSettle: settleTime - state.current.startedAt,
            },
            modality: state.current.lastInput,
            isBackward: isBackward(),
            ua: navigator.userAgent,
            metadata: {
              ...opts.metadata,
              ...(sectionTitle ? { section_title: sectionTitle } : {}),
              ...(sectionId ? { section_id: sectionId } : {}),
            },
          };

          opts.onEvent('text_selected', payload);
        }, settleMs);
      }

      // Note: We only fire selection_settled events, not continuous selection_update events
      // This reduces noise and only captures meaningful selection gestures

      // Selection cleared (end of gesture) — reset timers
      if (len === 0 && state.current.startedAt) {
        state.current.selectionId = undefined;
        state.current.startedAt = undefined;
        state.current.lastChangedAt = undefined;
        if (state.current.settleTimer) {
          clearTimeout(state.current.settleTimer);
        }
        state.current.settleTimer = undefined;
      }

      state.current.lastLen = len;
    };

    const unsubscribe = editor.onSelectionChange(handleSelectionChange);

    return () => {
      unsubscribe?.();
      if (state.current.settleTimer) {
        clearTimeout(state.current.settleTimer);
      }
    };
  }, [editor, opts, settleMs, throttleMs]);

  // Copy / cut handlers
  useEffect(() => {
    const root = editorRootRef.current;
    if (!editor || !root) return;

    const controller = new AbortController();
    const { signal } = controller;

    const handler = (type: 'text_copied') => async () => {
      const selTxt = editor.getSelectedText();
      const t = now();

      // Determine modality: prefer explicit keyboard copy chord detected recently
      const copiedByKeyboard = performance.now() - lastKeyboardCopyAtRef.current < 1500;
      let effectiveModality: 'mouse' | 'touch' | 'keyboard' | undefined = state.current.lastInput;
      if (copiedByKeyboard) {
        effectiveModality = 'keyboard';
      }
      if (!effectiveModality) {
        // Fallback for native copy where we couldn't capture modality reliably
        effectiveModality = 'keyboard';
      }

      // Try to split selection by sections (for multi-section selections)
      // Prefer shared bridge if available (JotDoc), otherwise use local sections
      let sectionSplits: Array<{
        sectionId: string;
        sectionTitle: string;
        selectedText: string;
        sectionTotalChars: number;
      }> = [];

      const root = editorRootRef.current as any;
      if (root?.__sectionAnalytics?.getSelectionBySections) {
        // Use shared bridge (JotDoc context)
        try {
          sectionSplits = root.__sectionAnalytics.getSelectionBySections();
        } catch (error) {
          console.error('[BlockNote Copy] Error getting sections from bridge:', error);
        }
      } else {
        // Use local sections (standalone BlockNote)
        sectionSplits = getSelectionBySections();
      }

      if (sectionSplits.length > 1) {
        // Multi-section selection - fire separate events for each section
        for (const split of sectionSplits) {
          const calculatedPercent =
            split.sectionTotalChars > 0 ? split.selectedText.length / split.sectionTotalChars : 0;

          const payload: SelectionMetrics = {
            selectionId: uuid(), // Unique ID for each section event
            docId: opts.docId,
            docVersion: opts.getDocVersion(),
            chars: {
              selected: split.selectedText.length,
              total: state.current.totalChars,
              percent: calculatedPercent,
            },
            timings: {
              startedAt: state.current.startedAt ?? t,
              lastChangedAt: state.current.lastChangedAt ?? t,
              copiedAt: t,
              msToCopy: state.current.startedAt ? t - state.current.startedAt : 0,
            },
            modality: effectiveModality,
            isBackward: isBackward(),
            ua: navigator.userAgent,
            metadata: {
              ...opts.metadata,
              section_title: split.sectionTitle,
              section_id: split.sectionId,
            },
          };

          opts.onEvent(type, payload);

          // Track in section analytics
          if (
            editorRootRef.current &&
            (editorRootRef.current as any).__sectionAnalytics?.trackCopy
          ) {
            try {
              (editorRootRef.current as any).__sectionAnalytics.trackCopy(split.sectionId);
            } catch {}
          }
        }
      } else {
        // Single section or no section detection - use original logic
        const { id: sectionId, title: sectionTitle } = getSectionInfoForSelection();

        // Also check for NoteSection tracking (for individual note sections)
        if (editorRootRef.current) {
          // Walk up the DOM tree to find a container with __noteSectionTrackSelect
          let current: HTMLElement | null = editorRootRef.current;
          while (current && current !== document.body) {
            if ((current as any).__noteSectionTrackCopy) {
              (current as any).__noteSectionTrackCopy();
              break;
            }
            current = current.parentElement;
          }
        }

        const sectionTotal = getSectionCharCountById(sectionId);
        const percentBase =
          sectionTotal && sectionTotal > 0 ? sectionTotal : state.current.totalChars;
        const calculatedPercent = percentBase ? selTxt.length / percentBase : 0;

        const payload: SelectionMetrics = {
          selectionId: state.current.selectionId ?? uuid(),
          docId: opts.docId,
          docVersion: opts.getDocVersion(),
          chars: {
            selected: selTxt.length,
            total: state.current.totalChars,
            percent: calculatedPercent,
          },
          timings: {
            startedAt: state.current.startedAt ?? t,
            lastChangedAt: state.current.lastChangedAt ?? t,
            copiedAt: t,
            msToCopy: state.current.startedAt ? t - state.current.startedAt : 0,
          },
          modality: effectiveModality,
          isBackward: isBackward(),
          ua: navigator.userAgent,
          metadata: {
            ...opts.metadata,
            ...(sectionTitle ? { section_title: sectionTitle } : {}),
            ...(sectionId ? { section_id: sectionId } : {}),
          },
        };

        opts.onEvent(type, payload);
      }
    };

    const onCopy = () => handler('text_copied')();
    const onCut = () => handler('text_copied')();

    root.addEventListener('copy', onCopy, { signal });
    root.addEventListener('cut', onCut, { signal });

    return () => {
      controller.abort();
    };
  }, [editor, editorRootRef, opts]);
}
